import React, { useCallback, useState } from 'react';

import { InfoWindow, Polygon } from '@react-google-maps/api';

import { ClusterOverlay, LatLongTuple } from '~/components/LERE/types';

// Convert [lat, lng] tuple to Google Maps LatLng object
function toLatLng(point: LatLongTuple): google.maps.LatLngLiteral {
  return { lat: point[0], lng: point[1] };
}

// Convert array of [lat, lng] tuples to Google Maps LatLng objects
function toLatLngArray(points: Array<LatLongTuple>): Array<google.maps.LatLngLiteral> {
  return points.map(toLatLng);
}

// Calculate centroid of a polygon for tooltip positioning
function getPolygonCentroid(paths: Array<Array<LatLongTuple>>): google.maps.LatLngLiteral | null {
  if (paths.length === 0 || paths[0].length === 0) {
    return null;
  }

  const firstPath = paths[0];
  let latSum = 0;
  let lngSum = 0;

  for (const point of firstPath) {
    latSum += point[0];
    lngSum += point[1];
  }

  return {
    lat: latSum / firstPath.length,
    lng: lngSum / firstPath.length,
  };
}

interface ClusterPolygonOverlayProps {
  overlay: ClusterOverlay;
  isSelected: boolean;
  fillOpacity: number;
  onSelect: (clusterId: number | null) => void;
}

export default function ClusterPolygonOverlay({
  overlay,
  isSelected,
  fillOpacity,
  onSelect,
}: ClusterPolygonOverlayProps) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = useCallback(() => {
    onSelect(isSelected ? null : overlay.cluster.cluster_id);
  }, [isSelected, onSelect, overlay.cluster.cluster_id]);

  const handleMouseOver = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseOut = useCallback(() => {
    setIsHovered(false);
  }, []);

  if (overlay.paths.length === 0) {
    return null;
  }

  const showTooltip = isSelected || isHovered;
  const centroid = showTooltip ? getPolygonCentroid(overlay.paths) : null;

  return (
    <>
      {overlay.paths.map((path, idx) => (
        <Polygon
          key={`${overlay.cluster.cluster_id}-${idx}`}
          paths={toLatLngArray(path)}
          options={{
            strokeColor: overlay.color,
            fillColor: overlay.color,
            fillOpacity,
            strokeOpacity: 1,
            strokeWeight: isSelected ? 3 : 1,
            clickable: true,
          }}
          onClick={handleClick}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        />
      ))}

      {showTooltip && centroid && (
        <InfoWindow
          position={centroid}
          options={{
            disableAutoPan: true,
            pixelOffset: new google.maps.Size(0, -10),
          }}
        >
          <div style={{ padding: '4px 8px', fontSize: '13px', fontWeight: 500 }}>{overlay.tooltipLabel}</div>
        </InfoWindow>
      )}
    </>
  );
}
